import type { SkuAssignment } from "./studioTypes";
import type { RuleSet } from "./storeRules";

export interface ShippedCdt {
  assignments: readonly SkuAssignment[];
  cdt0Order: readonly string[];
}

const sectionOrderOf = (rules: RuleSet): readonly string[] => {
  const order: string[] = [];
  for (const sku of [...rules.skus].sort((a, b) => a.walkRank - b.walkRank)) {
    if (!order.includes(sku.section)) order.push(sku.section);
  }
  return order;
};

/** Return a reason when stores would be generated from a tree the Studio did not ship. */
export const storeRulesDisagreement = (rules: RuleSet, shipped: ShippedCdt): string | null => {
  if (shipped.assignments.length > 0 && shipped.cdt0Order.length === 0) {
    return "the CDT has products but no cdt0 order";
  }
  const factsOf = new Map(rules.skus.map((sku) => [sku.skuId, sku]));
  const canonicalNames = new Map<string, Set<string>>();
  const addName = (name: string, cdt0: string) => {
    const matches = canonicalNames.get(name.trim()) ?? new Set<string>();
    matches.add(cdt0);
    canonicalNames.set(name.trim(), matches);
  };
  for (const name of rules.frozenCdt0Names) addName(name, name);
  for (const stratum of rules.strata) {
    if (stratum.bandValue?.trim()) {
      addName(`${stratum.section.trim()} ${stratum.bandValue.trim()}`, stratum.cdt0);
    }
  }
  const sectionOfShippedCdt0 = new Map<string, string>();

  for (const assignment of shipped.assignments) {
    const facts = factsOf.get(assignment.skuId);
    if (facts === undefined) {
      return `the rule set does not know product ${assignment.skuId}`;
    }
    const canonical = canonicalNames.get(assignment.cdt0.trim());
    if (canonical?.size !== 1 || !canonical.has(facts.cdt0)) {
      return `product ${assignment.skuId} ships under "${assignment.cdt0}" but the rule set puts it under "${facts.cdt0}"`;
    }

    const section = sectionOfShippedCdt0.get(assignment.cdt0);
    if (section === undefined) {
      sectionOfShippedCdt0.set(assignment.cdt0, facts.section);
    } else if (section !== facts.section) {
      return `"${assignment.cdt0}" mixes sections "${section}" and "${facts.section}"`;
    }
  }

  const rulesCdt0s = new Set(rules.cdt0Order);
  const sharedOrder = shipped.cdt0Order.filter((cdt0) => rulesCdt0s.has(cdt0));
  const sharedCdt0s = new Set(sharedOrder);
  const ruleSharedOrder = rules.cdt0Order.filter((cdt0) => sharedCdt0s.has(cdt0));
  if (
    sharedOrder.length !== ruleSharedOrder.length ||
    !sharedOrder.every((cdt0, index) => ruleSharedOrder[index] === cdt0)
  ) {
    return `the CDT orders cdt0s ${sharedOrder.join(", ")} but the rule set orders ${ruleSharedOrder.join(", ")}`;
  }

  const shippedSections: string[] = [];
  for (const cdt0 of shipped.cdt0Order) {
    const section = sectionOfShippedCdt0.get(cdt0);
    if (section === undefined || shippedSections.includes(section)) continue;
    shippedSections.push(section);
  }

  const ruleSections = sectionOrderOf(rules).filter((section) => shippedSections.includes(section));
  return shippedSections.join(" | ") === ruleSections.join(" | ")
    ? null
    : `the CDT orders sections ${shippedSections.join(", ")} but the rule set orders ${ruleSections.join(", ")}`;
};
